// 1. Imports de Angular core
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  effect,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

// 2. Imports de Angular modules
import { CommonModule, DOCUMENT } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';

// 3. Imports de Angular Material
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';

// 4. Imports de terceros
import { filter } from 'rxjs/operators';

// 5. Imports del proyecto
import { ChildrenItems, RouteInfo } from '../sidebar.routes';
import { MenuAccessService } from '../../admin/services/menu-access.service';
import { MenuItemResponse } from '../../admin/interface/menu-item.interface';

// 6. CONSTANTES
const SIDEBAR_MINI_CLASS = 'sidebar-mini';
const OAUTH_REDIRECT_ROUTE = '/auth/oauth-redirect';
const PERIODO_ACTIVO_ACTION = 'periodo-activo';
const PERIODO_ACTIVO_DIALOG_WIDTH = '480px';
const DEFAULT_ICON = 'circle';

/**
 * Ítems del menú dinámico que no navegan, sino que disparan una acción (ej. abrir un diálogo).
 * El backend (getMyMenu) no entrega el campo `action`, por lo que se resuelve por `code`.
 */
const ACTION_MENU_CODES: Record<string, string> = {
  'periodo-activo': 'periodo-activo',
};
/** Labels (normalizados) que también mapean a una acción, como fallback si el code difiere. */
const ACTION_MENU_LABELS: Record<string, string> = {
  'periodo activo': 'periodo-activo',
};
const LOGOUT_TOOLTIP = 'Cerrar sesión';
const COLLAPSE_TOOLTIP = 'Ocultar menú';
const EXPAND_TOOLTIP = 'Mostrar menú';

@Component({
  selector: 'app-sidebar-new',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatListModule,
    MatIconModule,
    MatExpansionModule,
    MatButtonModule,
    MatTooltipModule,
    MatDialogModule,
  ],
  templateUrl: './sidebar-new.component.html',
  styleUrls: ['./sidebar-new.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarNewComponent implements OnInit {
  // 7. Inyección de dependencias
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly menuService = inject(MenuAccessService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);

  // 8. Signals de estado
  readonly menuItems = signal<RouteInfo[]>([]);
  readonly userConected = signal<string>('');
  readonly collapsed = signal<boolean>(false);

  /** Título del grupo de nivel 1 actualmente expandido (single-open). */
  readonly openGroup = signal<string | null>(null);
  /** Título del subgrupo de nivel 2 actualmente expandido. */
  readonly openSubGroup = signal<string | null>(null);

  // 9. Tooltips derivados
  readonly toggleTooltip = COLLAPSE_TOOLTIP;
  readonly logoutTooltip = LOGOUT_TOOLTIP;

  // 13. Constructor (effects)
  constructor() {
    // Sincroniza la clase global `sidebar-mini` (gestiona el ancho del sidebar
    // y del main-panel desde los estilos globales del layout) con el estado local.
    effect(() => {
      const body = this.document.body;
      if (this.collapsed()) {
        body.classList.add(SIDEBAR_MINI_CLASS);
      } else {
        body.classList.remove(SIDEBAR_MINI_CLASS);
      }
    });
  }

  // 14. Lifecycle hooks
  ngOnInit(): void {
    const token = sessionStorage.getItem('token') ?? '';
    if (!token) {
      this.router.navigate([OAUTH_REDIRECT_ROUTE]);
      return;
    }

    this.userConected.set(sessionStorage.getItem('fullName') ?? '');
    this.collapsed.set(this.document.body.classList.contains(SIDEBAR_MINI_CLASS));

    this.loadMenu();

    // Mantener abierto el grupo que contiene la ruta activa al navegar.
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.openActiveGroup());
  }

  // 15. Métodos públicos (acciones del template)
  toggleCollapse(): void {
    this.collapsed.update((value) => !value);
  }

  onGroupOpened(title: string): void {
    // En modo colapsado, abrir un grupo primero expande el sidebar.
    if (this.collapsed()) {
      this.collapsed.set(false);
    }
    // Single-open: abrir uno cierra el resto.
    this.openGroup.set(title);
  }

  onGroupClosed(title: string): void {
    if (this.openGroup() === title) {
      this.openGroup.set(null);
    }
  }

  toggleSubGroup(title: string): void {
    this.openSubGroup.update((current) => (current === title ? null : title));
  }

  isGroupOpen(title: string): boolean {
    return !this.collapsed() && this.openGroup() === title;
  }

  isSubGroupOpen(title: string): boolean {
    return this.openSubGroup() === title;
  }

  onAction(action: string | undefined): void {
    if (action === PERIODO_ACTIVO_ACTION) {
      import(
        '../../proyecto/periodo-activo/periodo-activo-dialog/periodo-activo-dialog.component'
      ).then(({ PeriodoActivoDialogComponent }) => {
        this.dialog.open(PeriodoActivoDialogComponent, {
          width: PERIODO_ACTIVO_DIALOG_WIDTH,
        });
      });
    }
  }

  onLogout(): void {
    sessionStorage.clear();
    this.router.navigate([OAUTH_REDIRECT_ROUTE]);
  }

  shortname(): string {
    const fullName = this.userConected();
    if (!fullName) {
      return '';
    }
    return fullName
      .split(' ')
      .map((entry) => entry.charAt(0))
      .join('');
  }

  // 16. Métodos privados
  private loadMenu(): void {
    this.menuService
      .getMyMenu()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (items) => {
          this.menuItems.set(this.mapToRouteInfo(items));
          this.openActiveGroup();
        },
        error: () => {
          // Fallback: si el endpoint falla, el sidebar queda vacío y no bloquea la app.
          console.error('[SidebarNewComponent] No se pudo cargar el menú dinámico.');
        },
      });
  }

  /** Convierte el árbol del backend (MenuItemResponse) al formato RouteInfo del sidebar. */
  private mapToRouteInfo(items: MenuItemResponse[]): RouteInfo[] {
    return items.map((item) => {
      const hasChildren = !!item.children && item.children.length > 0;
      return {
        path: item.path ?? '',
        title: item.label,
        type: hasChildren ? 'sub' : 'link',
        icontype: item.icon ?? DEFAULT_ICON,
        collapse: hasChildren ? item.code : undefined,
        children: hasChildren
          ? item.children.map((child) => this.mapChild(child))
          : undefined,
      } as RouteInfo;
    });
  }

  private mapChild(child: MenuItemResponse): ChildrenItems {
    const hasChildren = !!child.children && child.children.length > 0;
    const action = this.resolveAction(child);
    return {
      path: child.path ?? '',
      title: child.label,
      type: hasChildren ? 'sub' : 'link',
      icontype: child.icon ?? undefined,
      collapse: hasChildren ? child.code : undefined,
      action,
      children: hasChildren
        ? child.children.map((sub) => ({
            path: sub.path ?? '',
            title: sub.label,
            icontype: sub.icon ?? undefined,
          }))
        : undefined,
    };
  }

  /**
   * Resuelve la acción asociada a un ítem del menú dinámico (que no navega).
   * Se busca primero por `code` y, como respaldo, por el `label` normalizado.
   */
  private resolveAction(item: MenuItemResponse): string | undefined {
    const byCode = item.code ? ACTION_MENU_CODES[item.code] : undefined;
    if (byCode) {
      return byCode;
    }
    return ACTION_MENU_LABELS[this.normalize(item.label)];
  }

  private normalize(value: string): string {
    return (value ?? '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .trim();
  }

  /** Abre automáticamente el grupo (y subgrupo) que contiene la ruta actual. */
  private openActiveGroup(): void {
    const currentUrl = this.router.url;

    for (const item of this.menuItems()) {
      if (item.type !== 'sub' || !item.children) {
        continue;
      }

      const matchingSubGroup = item.children.find((child) =>
        child.children?.some((sub) => !!sub.path && currentUrl.includes(sub.path)),
      );

      const hasDirectChild = item.children.some(
        (child) => !!child.path && currentUrl.includes(child.path),
      );

      if (hasDirectChild || matchingSubGroup) {
        this.openGroup.set(item.title);
        this.openSubGroup.set(matchingSubGroup ? matchingSubGroup.title : null);
        return;
      }
    }
  }
}
